// src/Pages/ReceptionistDashboard/Overview.js

import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Badge,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Stack,
  Avatar,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  EventAvailable as EventAvailableIcon,
  Phone as PhoneIcon,
  Notifications as NotificationsIcon,
  Message as MessageIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  LocalHospital as DoctorIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  MonetizationOn as RevenueIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  AssignmentInd as AssignmentIndIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { useSubscription } from '../../hooks/useSubscription';
import { useNavigate } from 'react-router-dom';
import {
  fetchReceptionistStats,
  fetchTodayAppointments,
  fetchClinicInfo,
} from '../../features/receptionist/receptionistSlice';

const ReceptionistOverview = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Redux state
  const { stats, statsLoading, statsError } = useSelector((state) => state.receptionist);
  const { todayAppointments: allTodayAppointments, appointmentsLoading } = useSelector((state) => state.receptionist);
  const { clinicInfo, clinicInfoLoading, clinicInfoError } = useSelector((state) => state.receptionist);
  
  const [notification, setNotification] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  
  // Get subscription info
  const { getSubscriptionInfo, checkFeatureAccess } = useSubscription();
  const subscriptionInfo = getSubscriptionInfo();
  
  // Show only first 5 appointments for overview
  const todayAppointments = allTodayAppointments.slice(0, 5);
  const loading = statsLoading || appointmentsLoading || clinicInfoLoading;

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      await Promise.all([
        dispatch(fetchReceptionistStats()),
        dispatch(fetchTodayAppointments()),
        dispatch(fetchClinicInfo()),
      ]);
      
      // Mock recent tasks (these would come from a task management API)
      setRecentTasks([
        { id: 1, title: 'Follow up with Dr. Kumar', status: 'pending', priority: 'high' },
        { id: 2, title: 'Update patient records', status: 'completed', priority: 'medium' },
        { id: 3, title: 'Schedule follow-up appointments', status: 'pending', priority: 'low' },
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setNotification({
        message: 'Failed to fetch dashboard data',
        severity: 'error'
      });
    }
  }, [dispatch]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);
  
  // Show error notifications
  useEffect(() => {
    if (statsError || clinicInfoError) {
      setNotification({
        message: statsError || clinicInfoError || 'Failed to fetch dashboard data',
        severity: 'error'
      });
    }
  }, [statsError, clinicInfoError]);

  const handleRefresh = () => {
    fetchDashboardData();
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Scheduled': return 'primary';
      case 'Arrived': return 'warning';
      case 'In Room': return 'info';
      case 'Completed': return 'success';
      case 'Canceled': return 'error';
      default: return 'default';
    }
  };

  const formatTime = (timeStr) => {
    return timeStr; // Already formatted from backend
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Welcome back!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {clinicInfo?.clinic?.name || 'Receptionist Dashboard'}
          </Typography>
          {subscriptionInfo && (
            <Chip 
              label={`${subscriptionInfo.planName} Plan`}
              color={subscriptionInfo.isActive ? 'success' : 'warning'}
              size="small"
              sx={{ mt: 1 }}
            />
          )}
        </Box>
        <IconButton onClick={handleRefresh} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <EventAvailableIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div">
                    {stats.today_appointments || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Today's Appointments
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <TimeIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div">
                    {stats.pending_appointments || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <CheckCircleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div">
                    {stats.completed_appointments || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                  <RevenueIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div">
                    ₹{(stats.total_revenue || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Revenue
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Today's Appointments */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" component="h2">
                  Today's Appointments
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  startIcon={<AssignmentIcon />}
                  onClick={() => navigate('/receptionist-dashboard/appointments')}
                >
                  View All
                </Button>
              </Box>
              
              {(todayAppointments || []).length === 0 ? (
                <Box textAlign="center" py={4}>
                  <EventAvailableIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    No appointments scheduled for today
                  </Typography>
                </Box>
              ) : (
                <List>
                  {(todayAppointments || []).map((appointment, index) => (
                    <React.Fragment key={appointment.id}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="subtitle1">
                                {appointment.patient_name}
                              </Typography>
                              <Chip 
                                label={appointment.status}
                                color={getStatusColor(appointment.status)}
                                size="small"
                              />
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Dr. {appointment.doctor_name} • {formatTime(appointment.scheduled_time)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ₹{appointment.consultation_fee}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < (todayAppointments || []).length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions & Info */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            {/* Quick Actions */}
            <Card>
              <CardContent>
                <Typography variant="h6" component="h3" gutterBottom>
                  Quick Actions
                </Typography>
                <Stack spacing={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={<PersonAddIcon />}
                    onClick={() => navigate('/receptionist-dashboard/registration')}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  >
                    Quick Patient Registration
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    color="primary"
                    startIcon={<AssignmentIndIcon />}
                    onClick={() => navigate('/receptionist-dashboard/check-in')}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  >
                    Patient Check-in
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CalendarIcon />}
                    onClick={() => navigate('/receptionist-dashboard/appointments')}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  >
                    Manage Appointments
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {/* Clinic Info */}
            {clinicInfo && (
              <Card>
                <CardContent>
                  <Typography variant="h6" component="h3" gutterBottom>
                    Clinic Information
                  </Typography>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Name:</strong> {clinicInfo.clinic?.clinic_name || clinicInfo.clinic?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Address:</strong> {clinicInfo.clinic?.address}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Phone:</strong> {clinicInfo.clinic?.phone_number || clinicInfo.clinic?.phone}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Doctors:</strong> {clinicInfo.doctors?.length || 0}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Recent Tasks */}
            <Card>
              <CardContent>
                <Typography variant="h6" component="h3" gutterBottom>
                  Recent Tasks
                </Typography>
                <List dense>
                  {recentTasks.map((task) => (
                    <ListItem key={task.id}>
                      <ListItemText
                        primary={task.title}
                        secondary={
                          <Chip 
                            label={task.status}
                            color={task.status === 'completed' ? 'success' : 'warning'}
                            size="small"
                          />
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      {/* Notification Snackbar */}
      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification?.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReceptionistOverview;
